import { IPromotionsClient } from './IPromotionsClient';
import {
  PromotionDto,
  PromotionListFilters,
  PromotionUpdateResult,
  PromotionUpsertDto,
} from './promotionTypes';

type ApiEnvelope<T> = {
  data?: T;
  success?: boolean;
  message?: string;
};

export class PromotionsClientApi implements IPromotionsClient {
  private readonly baseUrl: string;

  constructor(baseUrl: string = '/api/promotions') {
    this.baseUrl = baseUrl;
  }

  private async getHeaders(): Promise<HeadersInit> {
    const token = await window.Clerk?.session?.getToken?.({ template: 'Orderly_default' });

    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async extractErrorMessage(response: Response, fallback: string): Promise<string> {
    try {
      const errorData = await response.json();
      return errorData.message || errorData.detail || errorData.title || fallback;
    } catch {
      return fallback;
    }
  }

  private async readResponse<T>(response: Response, fallbackMessage: string): Promise<T> {
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, fallbackMessage));
    }

    const payload = await response.json() as ApiEnvelope<T> | T;

    if ('data' in (payload as ApiEnvelope<T>)) {
      const envelope = payload as ApiEnvelope<T>;

      if (envelope.success === false) {
        throw new Error(envelope.message || fallbackMessage);
      }

      return envelope.data as T;
    }

    return payload as T;
  }

  async getPromotions(filters?: PromotionListFilters): Promise<PromotionDto[]> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();

    if (filters?.searchText) params.append('searchText', filters.searchText);
    if (filters?.companyId) params.append('companyId', filters.companyId.toString());
    if (filters?.customerTypeId) params.append('customerTypeId', filters.customerTypeId.toString());
    if (filters?.catalogueId) params.append('catalogueId', filters.catalogueId.toString());
    if (filters?.status) params.append('status', filters.status);
    params.append('sort', filters?.sort || 'name');
    params.append('direction', filters?.direction || 'asc');
    params.append('pageNumber', (filters?.pageNumber ?? 0).toString());
    params.append('pageSize', (filters?.pageSize ?? 50).toString());

    const url = `${this.baseUrl}?${params.toString()}`;
    const response = await fetch(url, { headers });
    const paged = await this.readResponse<{ items: PromotionDto[] }>(response, 'Failed to fetch promotions.');
    return paged.items ?? [];
  }

  async getPromotion(id: number): Promise<PromotionDto | null> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}`, { headers });

    if (response.status === 404) return null;
    return this.readResponse<PromotionDto>(response, 'Failed to fetch promotion.');
  }

  async createPromotion(dto: PromotionUpsertDto): Promise<number> {
    const headers = await this.getHeaders();
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(dto),
    });
    return this.readResponse<number>(response, 'Failed to create promotion.');
  }

  async updatePromotion(id: number, dto: PromotionUpsertDto): Promise<PromotionUpdateResult> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to update promotion.'));
    }

    const payload = await response.json() as ApiEnvelope<PromotionUpdateResult> | PromotionUpdateResult;

    if ('data' in (payload as ApiEnvelope<PromotionUpdateResult>)) {
      const envelope = payload as ApiEnvelope<PromotionUpdateResult>;

      if (envelope.success === false) {
        throw new Error(envelope.message || 'Failed to update promotion.');
      }

      return {
        updated: envelope.data?.updated ?? true,
        blockedUnassignContentIds: envelope.data?.blockedUnassignContentIds ?? [],
        blockedUnassignSkus: envelope.data?.blockedUnassignSkus ?? [],
        message: envelope.message,
      };
    }

    return payload as PromotionUpdateResult;
  }

  async deletePromotion(id: number): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
      headers,
    });
    await this.readResponse<boolean>(response, 'Failed to delete promotion.');
  }
}
